package com.bikompanion.ui.home

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import org.maplibre.compose.camera.CameraAnimation
import org.maplibre.compose.camera.CameraUpdate
import org.maplibre.compose.map.MapState
import org.maplibre.spatialk.geojson.Position
import com.bikompanion.domain.StartPosition
import kotlin.math.abs

/** Barcelona, for a device whose time zone names no city. */
internal val FallbackStart = Position(longitude = 2.15, latitude = 41.4)

internal const val StartZoom = 12.0

// About ten metres: any pan or zoom-out moves the centre further than this.
private const val UntouchedToleranceDegrees = 1e-4

internal fun StartPosition.toPosition() = Position(longitude = longitude, latitude = latitude)

/**
 * Moves the camera when the start guess sharpens, which happens once the networks load.
 *
 * Only while the camera still sits where the previous guess put it: a rider who has panned, a
 * station link or a location fix already own it by then.
 */
@Composable
internal fun StartPositionEffect(
    startPosition: StartPosition?,
    cameraState: MapState,
    cameraOwnedElsewhere: () -> Boolean,
) {
    val currentCameraOwnedElsewhere by rememberUpdatedState(cameraOwnedElsewhere)
    var applied by remember { mutableStateOf(startPosition) }
    LaunchedEffect(startPosition) {
        val target = startPosition ?: return@LaunchedEffect
        if (target == applied) return@LaunchedEffect
        val previous = applied?.toPosition() ?: FallbackStart
        applied = target
        val center = cameraState.cameraPosition.center
        val untouched = abs(center.latitude - previous.latitude) < UntouchedToleranceDegrees &&
            abs(center.longitude - previous.longitude) < UntouchedToleranceDegrees
        if (!untouched || currentCameraOwnedElsewhere()) return@LaunchedEffect
        cameraState.animateCamera(CameraUpdate(center = target.toPosition()), CameraAnimation.Fly.Standard)
    }
}
